import { v4 as uuidv4 } from 'uuid';
import { 
  EngineChassisFormData, 
  RegistrationFormData, 
  TerminationFormData,
  ContinuationFormData,
  ApiResponse,
} from '../types';
import { useAuthStore } from '../store/authStore';
import axiosInstance from './axiosInstance';

// Always use real API endpoints regardless of environment
const USE_MOCK_DATA = false;

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken') || useAuthStore.getState().token;
};

// Helper to add authorization headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Authentication - always use real API
export const authenticateUser = async (email: string, password: string) => {
  try {
    console.log(`Authenticating user: ${email}`);
    
    // Always use the real API endpoint
    const response = await fetch('https://bankapi.vahanfin.com/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      console.error('Authentication failed:', responseData.message);
      return {
        success: false,
        message: responseData.message || 'Authentication failed'
      };
    }
    
    console.log('Authentication successful');
    return responseData;
  } catch (error) {
    console.error("Authentication error:", error);
    return {
      success: false,
      message: 'An error occurred during authentication'
    };
  }
};

// Add hypothecation by engine number - use real API
export const addVehicleByEngine = async (formData: EngineChassisFormData): Promise<ApiResponse<any>> => {
  try {
    // Always use real API
    const response = await fetch('https://bankapi.vahanfin.com/api/addition', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        regnNo: '',
        eng_no: formData.eng_no,
        chassisNo: formData.chassisNo,
        docUrl: formData.docUrl,
        hpc_from: formData.hpc_from,
        hpc_upto: formData.hpc_upto,
        bankName: useAuthStore.getState().user?.bankName,
        bankId: useAuthStore.getState().user?.bankId
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error("API error:", error);
    return {
      success: false,
      message: 'An error occurred while processing your request'
    };
  }
};

// Add hypothecation by registration number - use real API
export const addVehicleByRegistration = async (formData: RegistrationFormData): Promise<ApiResponse<any>> => {
  try {
    // Always use real API
    const response = await fetch('https://bankapi.vahanfin.com/api/addition', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        regnNo: formData.regnNo,
        chassisNo: formData.chassisNo,
        docUrl: formData.docUrl,
        hpc_from: formData.hpc_from,
        hpc_upto: formData.hpc_upto,
        bankName: useAuthStore.getState().user?.bankName,
        bankId: useAuthStore.getState().user?.bankId
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error("API error:", error);
    return {
      success: false,
      message: 'An error occurred while processing your request'
    };
  }
};

// Terminate hypothecation - use real API
export const terminateHypothecation = async (formData: TerminationFormData): Promise<ApiResponse<any>> => {
  try {
    // Always use real API
    const response = await fetch('https://bankapi.vahanfin.com/api/termination', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        regnNo: formData.regnNo,
        chassisNo: formData.chassisNo,
        docUrl: formData.docUrl,
        terminationDt: formData.terminationDt,
        bankName: useAuthStore.getState().user?.bankName,
        bankId: useAuthStore.getState().user?.bankId
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error("API error:", error);
    return {
      success: false,
      message: 'An error occurred while processing your request'
    };
  }
};

// Continue hypothecation - use real API
export const continueHypothecation = async (formData: ContinuationFormData): Promise<ApiResponse<any>> => {
  try {
    // Always use real API
    const response = await fetch('https://bankapi.vahanfin.com/api/continuation', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        regnNo: formData.regnNo,
        chassisNo: formData.chassisNo,
        docUrl: formData.docUrl,
        hpc_upto: formData.extendTo,
        bankName: useAuthStore.getState().user?.bankName,
        bankId: useAuthStore.getState().user?.bankId
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error("API error:", error);
    return {
      success: false,
      message: 'An error occurred while processing your request'
    };
  }
};

// Get hypothecation requests history - use real API
export const getHypothecationRequests = async ({ page = 1, limit = 50 } = {}): Promise<any> => {
  try {
    // Always use real API
    const currentUser = useAuthStore.getState().user;
    let apiUrl = 'https://bankapi.vahanfin.com/api/data';
    
    // Build query parameters based on user role and permissions
    const queryParams = [`page=${page}`, `limit=${limit}`];
    
    // Filter by bank for non-super_admin users
    if (currentUser && currentUser.role !== 'super_admin') {
      if (currentUser.bankId) {
        queryParams.push(`bankId=${currentUser.bankId}`);
      } else if (currentUser.bankName) {
        queryParams.push(`bankName=${encodeURIComponent(currentUser.bankName)}`);
      }
      
      // Filter by user ID for regular users (not admin or super_admin)
      if (!['super_admin', 'admin', 'manager', 'auditor'].includes(currentUser.role)) {
        queryParams.push(`userId=${currentUser.id}`);
      }
    }
    
    // Add query parameters to URL
    if (queryParams.length > 0) {
      apiUrl += `?${queryParams.join('&')}`;
    }
    
    console.log(`Fetching data from: ${apiUrl}`);
    
    const response = await fetch(apiUrl, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      console.error('API response not OK:', response.status, response.statusText);
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      console.error('API returned error:', data.error || data.message);
      return {
        success: false,
        message: data.error || data.message || 'Failed to fetch hypothecation data',
        addition: [],
        termination: [],
        continuation: []
      };
    }
    
    console.log('API data received:', {
      additions: (data.addition || []).length,
      terminations: (data.termination || []).length,
      continuations: (data.continuation || []).length,
    });
    
    return {
      success: data.success,
      addition: data.addition || [],
      termination: data.termination || [],
      continuation: data.continuation || [],
      pagination: data.pagination,
      statistics: data.statistics,
      message: data.message
    };
  } catch (error) {
    console.error("API error:", error);
    return {
      success: false,
      message: error.message || 'Failed to fetch hypothecation data',
      addition: [],
      termination: [],
      continuation: []
    };
  }
};

// Update password function
export const updatePassword = async (currentPassword: string, newPassword: string): Promise<ApiResponse> => {
  try {
    const user = useAuthStore.getState().user;
    
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }
    
    console.log('Sending password update request...');
    
    // Using fetch directly rather than axios for consistency with other API calls
    const response = await fetch('https://bankapi.vahanfin.com/api/update-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({
        email: user.email,
        currentPassword,
        newPassword
      }),
    });
    
    const responseData = await response.json();
    console.log('Password update response:', responseData);
    
    if (!response.ok) {
      console.error('Password update failed:', responseData);
      throw new Error(responseData.message || 'Failed to update password');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error updating password:', error);
    return {
      success: false,
      message: error.message || 'Failed to update password'
    };
  }
};

// Create a new bank
export const createBank = async (bankData: any): Promise<ApiResponse> => {
  try {
    const response = await fetch('https://bankapi.vahanfin.com/api/banks', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(bankData),
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to create bank');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error creating bank:', error);
    return {
      success: false,
      message: error.message || 'Failed to create bank'
    };
  }
};

// Update bank settings
export const updateBankSettings = async (bankId: string, data: any): Promise<ApiResponse> => {
  try {
    console.log(`Updating bank ${bankId} with data:`, data);
    
    const response = await fetch(`https://bankapi.vahanfin.com/api/banks/${bankId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to update bank settings');
    }
    
    console.log('Bank update successful:', responseData);
    return responseData;
  } catch (error) {
    console.error('Error updating bank settings:', error);
    return {
      success: false,
      message: error.message || 'Failed to update bank settings'
    };
  }
};

// Get bank settings
export const getBankSettings = async (bankId?: string): Promise<ApiResponse> => {
  try {
    console.log(`Fetching bank settings for bank ID: ${bankId || 'all'}`);
    
    const token = getAuthToken();
    let url = 'https://bankapi.vahanfin.com/api/banks';
    
    if (bankId) {
      url += `/${bankId}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP Error: ${response.status}`);
    }
    
    const responseData = await response.json();
    console.log('Received bank settings:', responseData);
    
    return responseData;
  } catch (error) {
    console.error('Error getting bank settings:', error);
    return {
      success: false,
      message: error.message || 'Failed to get bank settings'
    };
  }
};

// Delete a bank
export const deleteBank = async (bankId: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`https://bankapi.vahanfin.com/api/banks/${bankId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to delete bank');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error deleting bank:', error);
    return {
      success: false,
      message: error.message || 'Failed to delete bank'
    };
  }
};

// Update user profile
export const updateProfile = async (id: string, name: string, phone: string): Promise<ApiResponse> => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('User not authenticated');
    }
    
    console.log('Sending profile update request with user ID:', id);
    
    // Using fetch for consistency with other API calls
    const response = await fetch('https://bankapi.vahanfin.com/api/update-profile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        id,
        name,
        phone
      }),
    });
    
    const responseData = await response.json();
    console.log('Profile update response:', responseData);
    
    if (!response.ok) {
      console.error('Profile update failed:', responseData);
      throw new Error(responseData.message || 'Failed to update profile');
    }
    
    // Also update the auth store to ensure changes are visible immediately
    if (responseData.success && responseData.user) {
      useAuthStore.getState().updateUser(responseData.user);
    }
    
    return responseData;
  } catch (error) {
    console.error('Error updating profile:', error);
    return {
      success: false,
      message: error.message || 'Failed to update profile'
    };
  }
};

// Utility function for getting real data
export const getHypothecationData = () => {
  return getHypothecationRequests();
};
